import React, { useState } from 'react';
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MedicationIcon from '@mui/icons-material/Medication';
import ScienceIcon from '@mui/icons-material/Science';

interface GuidelinesSidebarProps {
  open: boolean;
  onClose: () => void;
}

const GuidelinesSidebar: React.FC<GuidelinesSidebarProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const [guidelinesSearch, setGuidelinesSearch] = useState('');
  const [isGuidelinesSearching, setIsGuidelinesSearching] = useState(false);

  const handleSearch = () => {
    if (guidelinesSearch.trim()) {
      setIsGuidelinesSearching(true);
      setTimeout(() => setIsGuidelinesSearching(false), 1500);
    }
  };

  const mockResults = [
    {
      title: 'Dermatologic Treatment Protocol',
      source: 'AAD Clinical Guidelines',
      relevance: 95,
      summary: 'Evidence-based recommendations for dermatological conditions including diagnostic criteria, treatment algorithms, and follow-up protocols.',
      icon: <MenuBookIcon />,
    },
    {
      title: 'Drug Interaction Database',
      source: 'FDA MedWatch',
      relevance: 88,
      summary: 'Comprehensive database of drug-drug interactions, contraindications, and safety alerts for dermatologic medications.',
      icon: <MedicationIcon />,
    },
    {
      title: 'Clinical Decision Support',
      source: 'UpToDate',
      relevance: 82,
      summary: 'Point-of-care clinical decision support with evidence-based recommendations and treatment pathways.',
      icon: <ScienceIcon />,
    },
  ];

  const quickActions = [
    'Treatment protocols',
    'Drug interactions',
    'Dosage guidelines',
    'Contraindications',
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="guidelines-drawer"
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '450px', md: '500px' },
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(to bottom, #0d0d0f 0%, #1a1a1e 100%)'
            : 'linear-gradient(to bottom, #ffffff 0%, #f5f7fa 100%)',
          borderLeft: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: theme.palette.mode === 'dark'
              ? 'rgba(0,200,83,0.1)'
              : 'rgba(0,200,83,0.05)',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #00C853 0%, #00A843 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 16px rgba(0,200,83,0.3)',
                }}
              >
                <LocalHospitalIcon sx={{ color: '#fff', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  Clinical Guidelines
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  AI-Powered Reasoning Agent
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={onClose}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  transform: 'rotate(90deg)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          {/* Search Box */}
          <TextField
            fullWidth
            placeholder="Search clinical guidelines, protocols, drug interactions..."
            value={guidelinesSearch}
            onChange={(e) => setGuidelinesSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.05)'
                  : '#ffffff',
                '&:hover fieldset': {
                  borderColor: '#00C853',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#00C853',
                  borderWidth: 2,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                  {isGuidelinesSearching ? (
                    <CircularProgress size={20} sx={{ color: '#00C853' }} />
                  ) : (
                    <ScienceIcon sx={{ color: theme.palette.text.secondary }} />
                  )}
                </Box>
              ),
            }}
          />
        </Box>

        {/* Content Area */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {guidelinesSearch.trim() && isGuidelinesSearching ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CircularProgress size={40} sx={{ color: '#00C853' }} />
              <Typography sx={{ mt: 2, color: theme.palette.text.secondary }}>
                Searching clinical guidelines...
              </Typography>
            </Box>
          ) : guidelinesSearch.trim() ? (
            <Stack spacing={3}>
              {/* Mock Search Results */}
              {mockResults.map((result, index) => (
                <Card
                  key={index}
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#00C853',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 24px rgba(0,200,83,0.15)',
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #00C853 0%, #00A843 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {React.cloneElement(result.icon, { sx: { color: '#fff', fontSize: 20 } })}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {result.title}
                        </Typography>
                        <Chip
                          label={`${result.relevance}%`}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(0,200,83,0.1)',
                            color: '#00C853',
                            fontWeight: 600,
                            fontSize: '11px',
                          }}
                        />
                      </Stack>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 1 }}>
                        {result.source}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '13px', lineHeight: 1.5 }}>
                        {result.summary}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '20px',
                  background: 'linear-gradient(135deg, #00C853 0%, #00A843 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  boxShadow: '0 8px 32px rgba(0,200,83,0.3)',
                }}
              >
                <LocalHospitalIcon sx={{ color: '#fff', fontSize: 40 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Clinical Guidelines Search
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 4, maxWidth: 300, mx: 'auto' }}>
                Search for clinical guidelines, treatment protocols, drug interactions, and evidence-based recommendations.
              </Typography>
              
              {/* Quick Action Chips */}
              <Stack spacing={1.5}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                  Quick Actions:
                </Typography>
                {quickActions.map((action, index) => (
                  <Chip
                    key={index}
                    label={action}
                    onClick={() => {
                      setGuidelinesSearch(action);
                      handleSearch();
                    }}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(0,200,83,0.1)'
                        : 'rgba(0,200,83,0.05)',
                      border: `1px solid ${theme.palette.divider}`,
                      fontWeight: 500,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(0,200,83,0.15)',
                        borderColor: '#00C853',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            background: theme.palette.mode === 'dark'
              ? 'rgba(0,0,0,0.2)'
              : 'rgba(0,0,0,0.02)',
          }}
        >
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textAlign: 'center', display: 'block' }}>
            MVP Demo - Full integration with medical databases coming soon
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

export default GuidelinesSidebar;
